// /Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/crm/leads/components/UpdateLeadForm.tsx
"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import useSWR from "swr";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronsUpDown } from "lucide-react";

import fetcher from "@/lib/fetcher";
import SuspenseLoading from "@/components/loadings/suspense";
import { cn } from "@/lib/utils";

// Define the User type based on your Users model
type User = {
  id: string;
  name?: string | null;
  email: string;
  role: string;
};

// Define the props type
type UpdateLeadFormProps = {
  initialData: any;
  setOpen: (value: boolean) => void;
};

// Adjusted form schema
const formSchema = z.object({
  id: z.string().min(5).max(100),
  first_name: z.string().nullable().optional(),
  middle_name: z.string().nullable().optional(),
  last_name: z.string().min(1, "Last name is required"),
  ssn: z
    .string()
    .regex(/^\d{3}-\d{2}-\d{4}$/, "Invalid SSN format, should be 'XXX-XX-XXXX'")
    .or(z.literal("")) // Allow empty string
    .nullable()
    .optional(),
  company: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().email("Invalid email format").nullable().optional(),
  advisorName: z.string().nullable().optional(),
  advisorEmail: z.string().email("Invalid email format").nullable().optional(),
  isReturningInvestor: z.boolean().nullable().optional(),
  ownsEquipmentCompany: z.boolean().nullable().optional(),
  dlfEngagementForLLC: z.boolean().nullable().optional(),
  preferredBusinessName: z.string().nullable().optional(),
  alternateBusinessName: z.string().nullable().optional(),
  stateOfFormation: z.string().nullable().optional(),
  equipmentUsagePlan: z.string().nullable().optional(),
  assetInterests: z.array(z.enum(["equipment", "open_to_others", "digital_advertising_screens", "gpu_data_servers"])).nullable().optional(),
  investmentAmount: z.number().nullable().optional(),
  downPaymentPercentage: z.number().nullable().optional(),
  businessType: z.string().nullable().optional(),
  businessFoundingDate: z.date().nullable().optional(),
  percentageOwnership: z.number().nullable().optional(),
  equipmentLLCName: z.string().nullable().optional(),
  equipmentLLCEIN: z.string().nullable().optional(),
  equipmentLLCAddress: z.string().nullable().optional(),
  equipmentLLCcounty: z.string().nullable().optional(),
  equipmentLLCcity: z.string().nullable().optional(),
  equipmentLLCzip: z.string().nullable().optional(),
  equipmentLLCstate: z.string().nullable().optional(),
  managedBy: z.string().nullable().optional(), // Keep as optional string for ID
  downPaymentAmount: z.number().nullable().optional(),
  cardNumber: z.string().nullable().optional(),
  cardName: z.string().nullable().optional(),
  expirationDate: z.date().nullable().optional(),
  cvv: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  street_address: z.string().nullable().optional(),
  business_owner: z.string().nullable().optional(),
  platformFeePercentage: z.preprocess(
      (val) => (val === "" ? null : val),
      z.number()
       .min(0, "Must be 0 or greater")
       .max(100, "Must be 100 or less")
       .nullable()
       .optional()
  ),
  // New fields for 2025 Profile Return
  profileReturning2025: z.boolean().nullable().optional(),
  profileReturnYear2025: z.number().nullable().optional(),
  investmentAmount2025: z.number().nullable().optional(),
});

type UpdateLeadFormValues = z.infer<typeof formSchema>;

const TARGET_MANAGER_NAMES = ["John McGowan", "Olivia Lacy"];

export function UpdateLeadForm({ initialData, setOpen }: UpdateLeadFormProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filteredManagerOptions, setFilteredManagerOptions] = useState<User[]>([]);

  const { data: accounts, isLoading: isLoadingAccounts } = useSWR("/api/crm/account", fetcher);
  const { data: users, isLoading: isLoadingUsers, error: usersError } = useSWR<User[]>("/api/user", fetcher);

  useEffect(() => {
    if (users) {
      const adminUsers = users.filter((user: User) => user.role === "admin");
      const specificManagers = adminUsers.filter((user: User) =>
        TARGET_MANAGER_NAMES.includes(user.name || "")
      );
      setFilteredManagerOptions(specificManagers);
    }
  }, [users]);

  const form = useForm<UpdateLeadFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: initialData?.id ?? "",
      first_name: initialData?.firstName ?? "",
      middle_name: initialData?.middleName ?? "",
      last_name: initialData?.lastName ?? "",
      ssn: initialData?.ssn ?? "",
      company: initialData?.company ?? "",
      phone: initialData?.phone ?? "",
      email: initialData?.email ?? "",
      street_address: initialData?.streetAddress ?? "",
      postal_code: initialData?.postalCode ?? "",
      state: initialData?.state ?? "",
      city: initialData?.city ?? "",
      advisorName: initialData?.advisorName ?? "",
      advisorEmail: initialData?.advisorEmail ?? "",
      isReturningInvestor: initialData?.isReturningInvestor ?? null,
      ownsEquipmentCompany: initialData?.ownsEquipmentCompany ?? null,
      dlfEngagementForLLC: initialData?.dlfEngagementForLLC ?? null,
      preferredBusinessName: initialData?.preferredBusinessName ?? "",
      alternateBusinessName: initialData?.alternateBusinessName ?? "",
      stateOfFormation: initialData?.stateOfFormation ?? "",
      equipmentUsagePlan: initialData?.equipmentUsagePlan ?? "",
      assetInterests: initialData?.assetInterests ?? [],
      investmentAmount: initialData?.investmentAmount ?? null,
      downPaymentPercentage: initialData?.downPaymentPercentage ?? null,
      businessType: initialData?.businessType ?? "",
      businessFoundingDate: initialData?.businessFoundingDate ? new Date(initialData.businessFoundingDate) : null,
      percentageOwnership: initialData?.percentageOwnership ?? null,
      equipmentLLCName: initialData?.equipmentLLCName ?? "",
      equipmentLLCEIN: initialData?.equipmentLLCEIN ?? "",
      equipmentLLCcity: initialData?.equipmentLLCcity ?? "",
      equipmentLLCzip: initialData?.equipmentLLCzip ?? "",
      equipmentLLCstate: initialData?.equipmentLLCstate ?? "",
      equipmentLLCcounty: initialData?.equipmentLLCcounty ?? "",
      equipmentLLCAddress: initialData?.equipmentLLCAddress ?? "",
      managedBy: initialData?.managedBy ?? null,
      downPaymentAmount: initialData?.downPaymentAmount ?? null,
      cardNumber: initialData?.cardNumber ?? "",
      cardName: initialData?.cardName ?? "",
      expirationDate: initialData?.expirationDate ? new Date(initialData.expirationDate) : null,
      cvv: initialData?.cvv ?? "",
      business_owner: initialData?.business_owner ?? null,
      platformFeePercentage: initialData?.platformFeePercentage ?? null,
      profileReturning2025: initialData?.profileReturning2025 ?? null,
      profileReturnYear2025: initialData?.profileReturnYear2025 ?? null,
      investmentAmount2025: initialData?.investmentAmount2025 ?? null,
    },
  });

  type AssetOptionValue = NonNullable<UpdateLeadFormValues['assetInterests']>[number];
  const assetOptions = useMemo<{ label: string; value: AssetOptionValue }[]>(
    () => [
      { label: "Equipment", value: "equipment" },
      { label: "Open to others", value: "open_to_others" },
      { label: "Digital Advertising Screens", value: "digital_advertising_screens" },
      { label: "GPU Data Servers", value: "gpu_data_servers" },
    ],
    []
  );

  const onSubmit = async (data: UpdateLeadFormValues) => {
    setIsLoading(true);
    console.log("Submitting data for update:", data);

    const apiData = {
        id: data.id,
        firstName: data.first_name,
        middleName: data.middle_name,
        lastName: data.last_name,
        ssn: data.ssn || null,
        company: data.company,
        phone: data.phone,
        email: data.email,
        streetAddress: data.street_address,
        postalCode: data.postal_code,
        state: data.state,
        city: data.city,
        advisorName: data.advisorName,
        advisorEmail: data.advisorEmail,
        isReturningInvestor: data.isReturningInvestor,
        ownsEquipmentCompany: data.ownsEquipmentCompany,
        dlfEngagementForLLC: data.dlfEngagementForLLC,
        preferredBusinessName: data.preferredBusinessName,
        alternateBusinessName: data.alternateBusinessName,
        stateOfFormation: data.stateOfFormation,
        equipmentUsagePlan: data.equipmentUsagePlan,
        assetInterests: Array.isArray(data.assetInterests)
          ? data.assetInterests
          : data.assetInterests
          ? [data.assetInterests]
          : [],
        investmentAmount: data.investmentAmount,
        downPaymentPercentage: data.downPaymentPercentage,
        businessType: data.businessType,
        businessFoundingDate: data.businessFoundingDate,
        percentageOwnership: data.percentageOwnership,
        equipmentLLCName: data.equipmentLLCName,
        equipmentLLCEIN: data.equipmentLLCEIN,
        equipmentLLCAddress: data.equipmentLLCAddress,
        equipmentLLCcounty: data.equipmentLLCcounty,
        equipmentLLCcity: data.equipmentLLCcity,
        equipmentLLCzip: data.equipmentLLCzip,
        equipmentLLCstate: data.equipmentLLCstate,
        managedBy: data.managedBy,
        downPaymentAmount: data.downPaymentAmount,
        cardNumber: data.cardNumber,
        cardName: data.cardName,
        expirationDate: data.expirationDate,
        cvv: data.cvv,
        business_owner: data.business_owner,
        platformFeePercentage: data.platformFeePercentage,
        profileReturning2025: data.profileReturning2025,
        profileReturnYear2025: data.profileReturnYear2025,
        investmentAmount2025: data.investmentAmount2025,
    };

    try {
      await axios.put(`/api/crm/leads`, apiData); // Use general PUT endpoint

      toast({
        title: "Success",
        description: "Lead updated successfully",
      });
      setOpen(false);
      router.refresh();
    } catch (error: any) {
      console.error("Update error:", error.response?.data || error.message);
      const fallbackMessage =
        error?.response?.data?.message ??
        error?.response?.data ??
        error?.message ??
        "An unexpected error occurred.";
      toast({
        variant: "destructive",
        title: "Error Updating Lead",
        description:
          typeof fallbackMessage === "string"
            ? fallbackMessage
            : JSON.stringify(fallbackMessage),
      });
    } finally {
      setIsLoading(false);
    }
  };

   if (isLoadingUsers || isLoadingAccounts) return <SuspenseLoading />;
   if (usersError) return <div className="p-4 text-center text-red-600">Error loading user data.</div>;
   if (!initialData) return <div className="p-4 text-center text-red-600">Error: Missing lead data.</div>;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="h-full px-4 space-y-6 overflow-y-auto md:px-10">

            {/* --- Personal Information Section --- */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Personal Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Managed By Dropdown */}
                    <FormField
                        control={form.control}
                        name="managedBy"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Managed By</FormLabel>
                                <Select
                                    disabled={isLoading || isLoadingUsers}
                                    onValueChange={(value) => field.onChange(value === "__NONE__" ? null : value)} // Handle custom none value
                                    value={field.value ?? undefined} // Pass undefined for placeholder
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Manager" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {/* You can add an explicit None option if desired, but ensure its value is NOT "" */}
                                        {/* <SelectItem value="__NONE__">-- None --</SelectItem> */}
                                        {filteredManagerOptions.length > 0 ? (
                                            filteredManagerOptions.map((manager) => (
                                                <SelectItem key={manager.id} value={manager.id}>
                                                    {manager.name || manager.email}
                                                </SelectItem>
                                            ))
                                        ) : (
                                            <SelectItem value="no-managers" disabled>No specific managers found</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    {/* Other Personal Info Fields */}
                    <FormField control={form.control} name="first_name" render={({ field }) => ( <FormItem><FormLabel>First name</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="John" value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="middle_name" render={({ field }) => ( <FormItem><FormLabel>Middle name (Optional)</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="M." value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="last_name" render={({ field }) => ( <FormItem><FormLabel>Last name</FormLabel><FormControl><Input required disabled={isLoading} {...field} placeholder="Doe" value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="ssn" render={({ field }) => ( <FormItem><FormLabel>SSN (Optional)</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="XXX-XX-XXXX" value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)}/>
                    <FormField
                      control={form.control}
                      name="assetInterests"
                      render={({ field }) => {
                        const currentValue = (Array.isArray(field.value) ? field.value : []) as AssetOptionValue[];
                        const formattedSelection = currentValue
                          .map((value) => assetOptions.find((option) => option.value === value)?.label ?? value)
                          .join(", ");

                        return (
                          <FormItem className="md:col-span-2">
                            <FormLabel>Asset purchase interests</FormLabel>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className={cn(
                                    "w-full justify-between",
                                    currentValue.length === 0 && "text-muted-foreground"
                                  )}
                                >
                                  {currentValue.length > 0 ? formattedSelection : "Select asset type(s)"}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-64">
                                {assetOptions.map((option) => (
                                  <DropdownMenuCheckboxItem
                                    key={option.value}
                                    checked={currentValue.includes(option.value)}
                                    onCheckedChange={(checked) => {
                                      const isChecked = checked === true;
                                      if (isChecked) {
                                        if (!currentValue.includes(option.value)) {
                                          field.onChange([...currentValue, option.value]);
                                        }
                                      } else {
                                        field.onChange(currentValue.filter((item) => item !== option.value));
                                      }
                                    }}
                                  >
                                    {option.label}
                                  </DropdownMenuCheckboxItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                </div>
            </div>

            {/* --- Address Information Section --- */}
            <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Address Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="street_address" render={({ field }) => ( <FormItem className="md:col-span-2"><FormLabel>Street Address</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="123 Main St" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="city" render={({ field }) => ( <FormItem><FormLabel>City</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Anytown" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="state" render={({ field }) => ( <FormItem><FormLabel>State</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="CA" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="postal_code" render={({ field }) => ( <FormItem><FormLabel>Postal Code</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="90210" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)} />
                </div>
            </div>

            {/* --- Contact Information Section --- */}
             <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Contact Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="email" render={({ field }) => ( <FormItem><FormLabel>E-mail</FormLabel><FormControl><Input type="email" disabled={isLoading} placeholder="john.doe@example.com" {...field} value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)} />
                    <FormField control={form.control} name="phone" render={({ field }) => ( <FormItem><FormLabel>Phone</FormLabel><FormControl><Input type="tel" disabled={isLoading} placeholder="+1 555-123-4567" {...field} value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)} />
                </div>
             </div>

            {/* --- Advisor Information Section --- */}
             <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Advisor Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="advisorName" render={({ field }) => ( <FormItem><FormLabel>Advisor Name</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Advisor's Full Name" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="advisorEmail" render={({ field }) => ( <FormItem><FormLabel>Advisor Email</FormLabel><FormControl><Input type="email" disabled={isLoading} {...field} placeholder="advisor@example.com" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                </div>
             </div>

             {/* --- Business Information Section --- */}
             <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Business Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="business_owner" render={({ field }) => ( <FormItem><FormLabel>Are you the business owner?</FormLabel><Select disabled={isLoading} onValueChange={field.onChange} value={field.value ?? undefined}><FormControl><SelectTrigger><SelectValue placeholder="Select Yes or No" /></SelectTrigger></FormControl><SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="preferredBusinessName" render={({ field }) => ( <FormItem><FormLabel>Preferred Business Name</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Acme Corp" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="alternateBusinessName" render={({ field }) => ( <FormItem><FormLabel>Alternate Business Name (Optional)</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Acme Inc." value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="stateOfFormation" render={({ field }) => ( <FormItem><FormLabel>State of Formation</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Delaware" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="businessType" render={({ field }) => ( <FormItem><FormLabel>Business Type</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="LLC, S-Corp, etc." value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="businessFoundingDate" render={({ field }) => ( <FormItem><FormLabel>Business Founding Date</FormLabel><FormControl><Input type="date" disabled={isLoading} value={field.value ? field.value.toISOString().split('T')[0] : ""} onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : null)}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="percentageOwnership" render={({ field }) => ( <FormItem><FormLabel>Percentage Ownership (%)</FormLabel><FormControl><Input type="number" min="0" max="100" step="0.01" disabled={isLoading} {...field} placeholder="100" onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)}/>
                </div>
            </div>

            {/* --- Investment Information Section --- */}
            <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Investment Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="isReturningInvestor" render={({ field }) => ( <FormItem className="flex flex-row items-start p-4 space-x-3 space-y-0 border rounded-md"><FormControl><input type="checkbox" className="w-4 h-4 mt-1 border-gray-300 rounded text-sky-600 focus:ring-sky-500" disabled={isLoading} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)}/></FormControl><div className="space-y-1 leading-none"><FormLabel>Is Returning Investor?</FormLabel></div><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="investmentAmount" render={({ field }) => ( <FormItem><FormLabel>Investment Amount ($)</FormLabel><FormControl><Input type="number" step="0.01" disabled={isLoading} {...field} placeholder="50000" onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="downPaymentPercentage" render={({ field }) => ( <FormItem><FormLabel>Down Payment (%)</FormLabel><FormControl><Input type="number" min="0" max="100" step="0.01" disabled={isLoading} {...field} placeholder="20" onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="downPaymentAmount" render={({ field }) => ( <FormItem><FormLabel>Down Payment Amount ($)</FormLabel><FormControl><Input type="number" step="0.01" disabled={isLoading} {...field} placeholder="10000" onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} value={field.value ?? ''}/></FormControl><FormMessage /></FormItem>)}/>
                    {/* Platform Fee Field */}
                    <FormField
                        control={form.control}
                        name="platformFeePercentage"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Platform Fee (%)</FormLabel>
                                <FormControl>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        disabled={isLoading}
                                        {...field}
                                        placeholder="e.g., 2.5"
                                        value={field.value ?? ''}
                                        onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            </div>

            {/* --- Equipment / LLC Information Section --- */}
            <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Equipment / LLC Information</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="ownsEquipmentCompany" render={({ field }) => ( <FormItem className="flex flex-row items-start p-4 space-x-3 space-y-0 border rounded-md"><FormControl><input type="checkbox" className="w-4 h-4 mt-1 border-gray-300 rounded text-sky-600 focus:ring-sky-500" disabled={isLoading} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)}/></FormControl><div className="space-y-1 leading-none"><FormLabel>Owns Equipment Company?</FormLabel></div><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="dlfEngagementForLLC" render={({ field }) => ( <FormItem className="flex flex-row items-start p-4 space-x-3 space-y-0 border rounded-md"><FormControl><input type="checkbox" className="w-4 h-4 mt-1 border-gray-300 rounded text-sky-600 focus:ring-sky-500" disabled={isLoading} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)}/></FormControl><div className="space-y-1 leading-none"><FormLabel>Engage DLF for LLC Formation?</FormLabel></div><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCName" render={({ field }) => ( <FormItem><FormLabel>Equipment LLC Name</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="My Equip LLC" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCEIN" render={({ field }) => ( <FormItem><FormLabel>Equipment LLC EIN</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="XX-XXXXXXX" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCAddress" render={({ field }) => ( <FormItem className="md:col-span-2"><FormLabel>Equipment LLC Address</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="456 Industrial Ave" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCcity" render={({ field }) => ( <FormItem><FormLabel>Equipment LLC City</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Workville" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCcounty" render={({ field }) => ( <FormItem><FormLabel>Equipment LLC County</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="Industry County" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCstate" render={({ field }) => ( <FormItem><FormLabel>Equipment LLC State</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="NV" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="equipmentLLCzip" render={({ field }) => ( <FormItem><FormLabel>Equipment LLC Zip</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="98765" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                </div>
            </div>

            {/* --- 2025 Profile Return Section --- */}
            <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">2025 Profile Return</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField 
                        control={form.control} 
                        name="profileReturning2025" 
                        render={({ field }) => ( 
                            <FormItem className="flex flex-row items-start p-4 space-x-3 space-y-0 border rounded-md">
                                <FormControl>
                                    <input 
                                        type="checkbox" 
                                        className="w-4 h-4 mt-1 border-gray-300 rounded text-sky-600 focus:ring-sky-500" 
                                        disabled={isLoading} 
                                        checked={!!field.value} 
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel>Returning in 2025?</FormLabel>
                                </div>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField 
                        control={form.control} 
                        name="profileReturnYear2025" 
                        render={({ field }) => ( 
                            <FormItem>
                                <FormLabel>Return Year (2025)</FormLabel>
                                <FormControl>
                                    <Input 
                                        type="number" 
                                        disabled={isLoading} 
                                        {...field} 
                                        placeholder="e.g., 3" 
                                        onChange={(e) => field.onChange(e.target.value === '' ? null : parseInt(e.target.value))} 
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField 
                        control={form.control} 
                        name="investmentAmount2025" 
                        render={({ field }) => ( 
                            <FormItem>
                                <FormLabel>2025 Investment Amount ($)</FormLabel>
                                <FormControl>
                                    <Input 
                                        type="number" 
                                        step="0.01" 
                                        disabled={isLoading} 
                                        {...field} 
                                        placeholder="50000" 
                                        onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} 
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            </div>

            {/* --- Payment Information Section --- */}
             <div>
                <Separator className="my-6" />
                <h2 className="mb-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Payment Information (Optional)</h2>
                <p className="mb-4 text-sm text-muted-foreground">Only fill if necessary. Ensure secure handling.</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField control={form.control} name="cardNumber" render={({ field }) => ( <FormItem><FormLabel>Card Number</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="•••• •••• •••• 1234" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="cardName" render={({ field }) => ( <FormItem><FormLabel>Name on Card</FormLabel><FormControl><Input disabled={isLoading} {...field} placeholder="John M Doe" value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="expirationDate" render={({ field }) => ( <FormItem><FormLabel>Expiration Date</FormLabel><FormControl><Input type="month" disabled={isLoading} value={field.value ? field.value.toISOString().slice(0, 7) : ""} onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value + '-01T00:00:00Z') : null)}/></FormControl><FormMessage /></FormItem>)}/>
                    <FormField control={form.control} name="cvv" render={({ field }) => ( <FormItem><FormLabel>CVV</FormLabel><FormControl><Input type="password" disabled={isLoading} {...field} placeholder="•••" maxLength={4} value={field.value ?? ""}/></FormControl><FormMessage /></FormItem>)}/>
                </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 pb-4 mt-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                className="px-6 py-2 text-white rounded bg-gradient-to-b from-blue-500 to-blue-800 hover:from-blue-600 hover:to-blue-900"
                disabled={isLoading}
                type="submit"
              >
                {isLoading ? (
                    <span className="flex items-center">
                        <svg className="w-5 h-5 mr-2 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        Saving...
                    </span>
                 ) : ( "Update Profile" )}
              </Button>
            </div>

      </form>
    </Form>
  );
}
